import {
  Activity,
  ArrowLeftRight,
  BadgeCheck,
  Bell,
  Bot,
  Building2,
  Gauge,
  History,
  Home,
  KeyRound,
  Layers,
  LifeBuoy,
  MessagesSquare,
  Plug,
  ScrollText,
  Send,
  Server,
  Settings,
  Shapes,
  ShieldCheck,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react';
import type { MeResponse } from '@hefesto/contracts';
import { StatusPill, type ShellNavGroup, type ShellNavItem } from '@hefesto/ui';
import {
  ACCESS_READERS,
  hasPlatform,
  isInternal,
  mfaPending,
  PLAN_READERS,
  SYSTEM_READERS,
} from './access';
import { portalCan, portalDestinations, portalPath, type PortalContext } from './portal';

/**
 * Navegación DERIVADA del Principal (`/api/v1/me`): nunca una lista fija (docs/MODULE-SYSTEM.md §6,
 * DESIGN-SYSTEM §6.2). Superadmin según los permisos `platform.*`; lo que llega en otra fase se
 * muestra deshabilitado con su fase SOLO si el usuario tendría permiso. Los controles reales están
 * en el backend.
 *
 * (03A) Dentro de `/t/:empresa` (`portal`), la navegación es la de ESA empresa: herramientas
 * desplegadas ∩ habilitadas ∩ con permiso propio, y las secciones según los permisos de esa
 * empresa (nunca se mezclan con los de otra). (03B) En tres grupos: la empresa (herramientas),
 * Administración y Ayuda.
 */
export function navFor(me: MeResponse, portal?: PortalContext | null): ShellNavGroup[] {
  if (portal) return portalNav(me, portal);
  const groups: ShellNavGroup[] = [
    {
      id: 'hefesto',
      label: 'Hefesto',
      items: [
        { id: 'home', label: 'Inicio', href: '/', icon: <Home aria-hidden />, end: true },
        {
          id: 'status',
          label: 'Estado del sistema',
          href: '/estado',
          icon: <Activity aria-hidden />,
        },
      ],
    },
  ];

  if (isInternal(me)) {
    const can = (p: string) => hasPlatform(me, p);
    const mfa = mfaPending(me);
    const admin: ShellNavItem[] = [];
    if (can('platform.dashboard.view'))
      admin.push({
        id: 'admin',
        label: 'Panel',
        href: '/admin',
        icon: <Gauge aria-hidden />,
        end: true,
      });
    if (can('platform.support.view'))
      admin.push({
        id: 'support',
        label: 'Soporte',
        href: '/admin/support',
        icon: <LifeBuoy aria-hidden />,
      });
    if (can('platform.clients.view'))
      admin.push({
        id: 'clients',
        label: 'Clientes',
        href: '/admin/clients',
        icon: <Building2 aria-hidden />,
      });
    if (PLAN_READERS.some(can))
      admin.push({
        id: 'plans',
        label: 'Planes',
        href: '/admin/plans',
        icon: <Layers aria-hidden />,
      });
    if (ACCESS_READERS.some(can))
      admin.push({
        id: 'access',
        label: 'Accesos a datos',
        href: '/admin/access',
        icon: <KeyRound aria-hidden />,
      });
    if (can('platform.members.view'))
      admin.push({ id: 'team', label: 'Equipo', href: '/admin/team', icon: <Users aria-hidden /> });
    if (can('platform.audit.view'))
      admin.push({
        id: 'audit',
        label: 'Auditoría',
        href: '/admin/audit',
        icon: <ScrollText aria-hidden />,
      });
    if (SYSTEM_READERS.some(can))
      admin.push({
        id: 'system',
        label: 'Sistema',
        href: '/admin/system',
        icon: <Server aria-hidden />,
      });
    if (can('platform.channels.usage.view'))
      admin.push({
        id: 'whatsapp',
        label: 'WhatsApp',
        href: '/admin/whatsapp',
        icon: <MessagesSquare aria-hidden />,
      });
    if (can('platform.actions.approve'))
      admin.push({
        id: 'approvals',
        label: 'Aprobaciones',
        href: '/admin/aprobaciones',
        icon: <BadgeCheck aria-hidden />,
      });
    // (05F) Agente interno por Telegram: quien lo usa o quien lo administra.
    if (can('platform.agent.use') || can('platform.service_accounts.manage'))
      admin.push({
        id: 'agent',
        label: 'Jarvis',
        href: '/admin/agente',
        icon: <Send aria-hidden />,
      });
    if (can('platform.service_accounts.manage'))
      admin.push({
        id: 'service-accounts',
        label: 'Agentes y claves',
        href: '/admin/service-accounts',
        icon: <Bot aria-hidden />,
      });
    admin.push({
      id: 'security',
      label: 'Seguridad',
      href: '/admin/security',
      icon: <ShieldCheck aria-hidden />,
      badge: mfa.needed ? (
        <StatusPill tone="warn" className="px-2 py-0 text-[10.5px]">
          MFA
        </StatusPill>
      ) : undefined,
    });
    groups.push({ id: 'superadmin', label: "Mot's · Superadmin", items: admin });
  }

  const companies = portalDestinations(me);
  if (companies.length > 0)
    groups.push({
      id: 'companies',
      label: 'Tus empresas',
      items: companies.map((c) => ({
        id: `t:${c.slug}`,
        label: c.name,
        href: portalPath(c.slug),
        icon: <Building2 aria-hidden />,
        badge:
          c.kind === 'support_access' ? (
            <StatusPill tone="info" className="px-2 py-0 text-[10.5px]">
              Soporte
            </StatusPill>
          ) : undefined,
      })),
    });

  return groups;
}

/** (05B2) Quién ve Integraciones → WhatsApp (además de los permisos de integraciones). */
export const CHANNEL_SETTINGS_READERS = [
  'channels.connections.manage',
  'channels.usage.view',
  'channels.templates.manage',
] as const;

/** (05B2) La capacidad `channels` se puede usar (habilitada o en solo lectura). */
export function channelsUsable(ctx: PortalContext): boolean {
  const s = ctx.capabilities?.channels;
  return s === 'enabled' || s === 'read_only';
}

/** Navegación de UNA empresa (dentro de `/t/:empresa`). */
function portalNav(me: MeResponse, ctx: PortalContext): ShellNavGroup[] {
  const can = (p: string) => portalCan(ctx, p);
  const at = (path: string) => portalPath(ctx.slug, path);
  const items: ShellNavItem[] = [];
  if (can('tenant.dashboard.view'))
    items.push({
      id: 'p-home',
      label: 'Tus herramientas',
      href: at(''),
      icon: <Shapes aria-hidden />,
      end: true,
    });
  // (05B2) Inbox de WhatsApp: pestaña propia (velocidad; no carga nada del Portal). Solo con la
  // capacidad `channels` habilitada (o en solo lectura): nunca un destino que diga "no habilitado".
  if (can('channels.inbox.view') && channelsUsable(ctx))
    items.push({
      id: 'p-inbox',
      label: 'Conversaciones',
      href: `/inbox/${ctx.slug}`,
      icon: <MessagesSquare aria-hidden />,
      newTab: true,
    });
  for (const tool of ctx.tools)
    items.push({
      id: `p-tool-${tool.slug}`,
      label: tool.name,
      href: at(`/${tool.slug}`),
      icon: <Wrench aria-hidden />,
      badge:
        tool.state === 'read_only' ? (
          <StatusPill tone="warn" className="px-2 py-0 text-[10.5px]">
            Lectura
          </StatusPill>
        ) : undefined,
    });

  const admin: ShellNavItem[] = [];
  if (can('tenant.dashboard.view') || can('tenant.profile.edit'))
    admin.push({
      id: 'p-company',
      label: 'Empresa',
      href: at('/empresa'),
      icon: <Building2 aria-hidden />,
    });
  if (can('tenant.members.view') || can('tenant.members.manage'))
    admin.push({ id: 'p-team', label: 'Equipo', href: at('/equipo'), icon: <Users aria-hidden /> });
  if (can('tenant.roles.manage') || can('tenant.members.manage'))
    admin.push({
      id: 'p-roles',
      label: 'Roles',
      href: at('/roles'),
      icon: <UserCog aria-hidden />,
    });
  if (can('tenant.activity.view'))
    admin.push({
      id: 'p-activity',
      label: 'Actividad',
      href: at('/actividad'),
      icon: <History aria-hidden />,
    });
  if (can('tenant.actions.approve'))
    admin.push({
      id: 'p-approvals',
      label: 'Aprobaciones',
      href: at('/aprobaciones'),
      icon: <BadgeCheck aria-hidden />,
    });
  if (
    can('tenant.integrations.view') ||
    can('tenant.integrations.manage') ||
    (channelsUsable(ctx) && CHANNEL_SETTINGS_READERS.some(can))
  )
    admin.push({
      id: 'p-integrations',
      label: 'Integraciones',
      href: at('/integraciones'),
      icon: <Plug aria-hidden />,
    });
  // Las claves API se gestionan (incluso para verlas) con permiso de escritura: nunca en solo lectura.
  if (can('tenant.api_keys.manage') && !ctx.readOnly)
    admin.push({
      id: 'p-keys',
      label: 'Claves API',
      href: at('/claves'),
      icon: <KeyRound aria-hidden />,
    });
  if (can('tenant.settings.edit') || can('tenant.support_access.manage'))
    admin.push({
      id: 'p-settings',
      label: 'Configuración',
      href: at('/configuracion'),
      icon: <Settings aria-hidden />,
    });

  const help: ShellNavItem[] = [];
  if (can('support.view') || can('support.view_all') || can('support.create'))
    help.push({
      id: 'p-support',
      label: 'Soporte',
      href: at('/soporte'),
      icon: <LifeBuoy aria-hidden />,
    });
  if (can('notifications.view'))
    help.push({
      id: 'p-notices',
      label: 'Avisos',
      href: at('/avisos'),
      icon: <Bell aria-hidden />,
    });

  const groups: ShellNavGroup[] = items.length ? [{ id: 'portal', label: ctx.name, items }] : [];
  if (admin.length) groups.push({ id: 'portal-admin', label: 'Administración', items: admin });
  if (help.length) groups.push({ id: 'portal-help', label: 'Ayuda', items: help });
  if (portalDestinations(me).length > 1 || isInternal(me))
    groups.push({
      id: 'hefesto',
      label: 'Hefesto',
      items: [
        {
          id: 'switch',
          label: 'Cambiar de empresa',
          href: '/select-tenant',
          icon: <ArrowLeftRight aria-hidden />,
        },
        ...(isInternal(me)
          ? [
              hasPlatform(me, 'platform.dashboard.view')
                ? {
                    id: 'admin',
                    label: 'Superadmin',
                    href: '/admin',
                    icon: <Gauge aria-hidden />,
                    end: true,
                  }
                : { id: 'home', label: 'Inicio', href: '/', icon: <Home aria-hidden />, end: true },
            ]
          : []),
      ],
    });
  return groups;
}
